'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { RoomInfoPanel } from '@/components/public/room-info-panel';
import { ImageLightbox } from '@/components/public/image-lightbox';
import type { LightboxImage } from '@/components/public/image-lightbox';
import type { Room } from '@/types/database';

export default function Room9Content({ room }: { room: Room }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useTranslations('roomContent.room-9');

  const GALLERY_IMAGES: LightboxImage[] = [
    { src: '/design-images/rooms/room-9/detail.jpg', alt: t('alt.detail'), width: 1709, height: 2560 },
  ];

  return (
    <div id="room-content" className="w-full flex flex-col">
      {/* White spacer between hero and first section */}
      <div className="bg-white h-2 w-full" />

      {/* Section: Room Benefits */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src="/design-images/rooms/room-9/detail.jpg"
                alt={t('alt.detail')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="w-full flex items-center py-6 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-4 text-4xl md:text-5xl">{t('benefits.title')}</h1>
                    <p className="mb-4">{t('benefits.p1')}</p>
                </div>
                <RoomInfoPanel room={room} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Story — coming soon placeholder */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="w-full flex items-center py-8 md:py-12 px-6 md:px-8 lg:px-12">
            <div className="text-black w-full max-w-2xl">
              <div className="tiptap-content text-base md:text-lg leading-relaxed">
                <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('story.title')}</h1>
                <div className="w-16 h-1 bg-[#801515] mb-8" />
                <p className="text-lg md:text-xl leading-relaxed font-light italic text-gray-700">{t('story.p1')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Fullscreen gallery lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={GALLERY_IMAGES}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
